import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { Button } from 'ui';
import { useLoading } from 'ui/contexts/overlay/Loading';
import { useAuth } from 'auth/contexts/Auth';
import { AUTHENTICATE } from 'auth/graphql/authenticateQuery';
import EmailField from './EmailField';
import PasswordField from './PasswordField';
import { MdArrowForward } from 'react-icons/md';
import useToastContext from 'ui/hooks/useToast';
import { ToastProps } from 'ui/contexts/overlay/Toast';

type SignInUser = {
  userEmail?: string;
  userPassword?: string;
};

const AUTH_ERROR_TOAST: ToastProps = {
  text: 'Preencha os campos de login e senha',
  title: 'Algo deu errado!',
  variant: 'danger',
};

export default function LoginForm() {
  const { addToast } = useToastContext();
  const { showLoading, closeLoading } = useLoading();
  const { storeCredentials, token } = useAuth();
  const [user, setUser] = React.useState<SignInUser>();
  const [authenticate, { loading, data, error }] = useLazyQuery(AUTHENTICATE);

  const handleSubmitForm = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!!user?.userEmail && !!user?.userPassword) {
      authenticate({
        variables: {
          userEmail: user.userEmail,
          userPassword: user.userPassword,
        },
      });
    } else {
      addToast(AUTH_ERROR_TOAST);
    }
  };

  React.useEffect(() => {
    if (loading) {
      showLoading();
      return;
    }

    if (!token && !!data?.authenticate?.userToken) {
      storeCredentials({
        token: data.authenticate.userToken,
        user: data.authenticate.user,
      });
    }

    closeLoading();
  }, [closeLoading, data, loading, showLoading, storeCredentials, token]);

  if (token !== null) return <Redirect to="/dashboard" />;

  return (
    <form
      onSubmit={handleSubmitForm}
      className="flex flex-col gap-8 w-full h-full"
      data-testid="login-form"
    >
      <EmailField
        id="email"
        type="text"
        label="Email"
        name="userEmail"
        error={error && ' '}
        autoComplete="email"
        placeholder="email@nexenergy.com.br"
        defaultValue={user?.userEmail}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setUser({ ...user, userEmail: event.target.value })
        }
      />

      <PasswordField
        label="Senha"
        type="password"
        name="userPassword"
        id="current-password"
        placeholder="***************"
        autoComplete="current-password"
        error={error ? 'Email ou senha inválido!' : ''}
        defaultValue={user?.userPassword}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setUser({ ...user, userPassword: event.target.value })
        }
      />

      <div className="text-center w-full flex items-center justify-start">
        <Button className="w-auto" type="submit">
          <p className="font-medium text-sm">Logar na conta</p>
          <MdArrowForward size={20} />
        </Button>
      </div>
    </form>
  );
}
